import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { useAddComment, useCategories, useZoneUsers } from './api.ts';
import { zoneErrorMessage } from './errors.ts';
import { absoluteTime, relativeTime } from './time.ts';
import { STATUS_LABEL, type IssueDetail, type IssueEvent } from './types.ts';
import { useAuthStore } from '../../store/auth.ts';

const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

function iconFor(ev: IssueEvent): string {
  switch (ev.type) {
    case 'CREATED': return 'report';
    case 'ASSIGNED':
    case 'REASSIGNED': return 'assignment_ind';
    case 'REOPENED': return 'replay';
    case 'COMMENT': return 'chat';
    default:
      if (ev.toStatus === 'IN_PROGRESS') return 'play_arrow';
      if (ev.toStatus === 'RESOLVED') return 'task_alt';
      if (ev.toStatus === 'CLOSED') return 'done_all';
      return 'check_circle';
  }
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

export function IssueTimeline({ issue, variant = 'inline' }: {
  issue: IssueDetail;
  /** `inline`: flows with the page (phones). `panel`: Planner-style side chat — fills its
   *  container, messages scroll, composer pinned at the bottom. */
  variant?: 'inline' | 'panel';
}): JSX.Element {
  const me = useAuthStore((s) => s.user?.id);
  const listRef = useRef<HTMLOListElement>(null);
  // Old events store ids only; resolve names from the lists the page already needs.
  const users = useZoneUsers();
  const categories = useCategories(true);
  const userName = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u.name])), [users.data]);
  const categoryName = useMemo(() => new Map((categories.data ?? []).map((c) => [c.id, c.name])), [categories.data]);

  const add = useAddComment();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const describe = (ev: IssueEvent): string => {
    const who = ev.actor.name;
    switch (ev.type) {
      case 'CREATED': return `${who} reported this issue`;
      case 'ASSIGNED':
      case 'REASSIGNED': {
        const aId = str(ev.metadata?.assigneeId);
        const cId = str(ev.metadata?.categoryId);
        // Newer events carry names in metadata; older ones only have ids, so fall back to lookups.
        const assignee = str(ev.metadata?.assigneeName) ?? ((aId && (userName.get(aId) ?? (aId === issue.assigneeId ? issue.assignee?.name : null))) || 'staff');
        const category = str(ev.metadata?.categoryName) ?? ((cId && (categoryName.get(cId) ?? (cId === issue.categoryId ? issue.category?.name : null))) || null);
        return `${who} ${ev.type === 'ASSIGNED' ? 'assigned to' : 'reassigned to'} ${assignee}${category ? ` · ${category}` : ''}`;
      }
      case 'REOPENED': return `${who} reopened`;
      case 'COMMENT': return who;
      default:
        if (ev.toStatus === 'RESOLVED') return `${who} resolved this`;
        if (ev.toStatus === 'CLOSED') return `${who} confirmed it fixed`;
        return `${who} changed status to ${ev.toStatus ? STATUS_LABEL[ev.toStatus] : 'unknown'}`;
    }
  };

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const text = comment.trim();
    if (!text) return;
    setError(null);
    add.mutate(
      { id: issue.id, comment: text },
      { onSuccess: () => setComment(''), onError: (err) => setError(zoneErrorMessage(err)) },
    );
  };

  const events = [...issue.events].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  const panel = variant === 'panel';

  // Panel opens on the newest message and follows new ones, like a chat.
  useEffect(() => {
    if (panel && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [panel, events.length]);

  const item = (ev: IssueEvent): JSX.Element => {
    const time = (
      <time dateTime={ev.createdAt} title={absoluteTime(ev.createdAt)} className="whitespace-nowrap text-xs text-muted-foreground">
        {relativeTime(ev.createdAt)}
      </time>
    );
    if (ev.type === 'COMMENT') {
      const mine = ev.actor.id === me;
      return (
        <li key={ev.id} className={`flex gap-3 ${mine ? 'flex-row-reverse' : ''}`}>
          <span aria-hidden className="grid size-8 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-xs font-medium text-[hsl(var(--on-secondary-container))]">
            {initials(ev.actor.name)}
          </span>
          <div className={`min-w-0 max-w-[85%] ${mine ? 'text-end' : ''}`}>
            <p className="text-sm"><span className="font-medium">{mine ? 'You' : ev.actor.name}</span> <span className="ms-1">{time}</span></p>
            <p className={`mt-1 inline-block whitespace-pre-wrap break-words rounded-2xl border border-[hsl(var(--border)/0.7)] px-4 py-2.5 text-start text-[0.95rem] leading-relaxed ${mine ? 'rounded-se-sm bg-[hsl(var(--foreground)/0.04)]' : 'rounded-ss-sm'}`}>
              {ev.comment}
            </p>
          </div>
        </li>
      );
    }
    return (
      <li key={ev.id} className="flex gap-3">
        <span className="grid size-8 shrink-0 place-items-center text-muted-foreground"><Icon name={iconFor(ev)} size={20} /></span>
        <p className="min-w-0 flex-1 break-words pt-1 text-[0.95rem] leading-relaxed">
          {describe(ev)}
          {ev.comment && <span className="text-muted-foreground"> — {ev.comment}</span>}
          <span className="ms-2">{time}</span>
        </p>
      </li>
    );
  };

  return (
    <section
      className={panel ? 'flex h-full flex-col overflow-hidden rounded-3xl border border-[hsl(var(--border)/0.8)]' : 'space-y-4'}
      aria-label="Activity"
    >
      <h2 className={panel ? 'flex items-center gap-2 border-b border-[hsl(var(--border)/0.6)] px-5 py-4 text-base font-medium' : 'text-sm font-medium text-primary'}>
        {panel && <Icon name="chat" size={20} className="text-primary" />}
        Activity &amp; comments
        {panel && <span className="ms-auto text-sm font-normal text-muted-foreground">{events.length}</span>}
      </h2>
      <ol ref={listRef} className={panel ? 'flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5' : 'space-y-5'}>
        {events.map(item)}
      </ol>

      <form onSubmit={submit} className={panel ? 'space-y-2 border-t border-[hsl(var(--border)/0.6)] p-4' : 'space-y-2'}>
        <label className="sr-only" htmlFor={panel ? 'comment-panel' : 'comment'}>Add a comment</label>
        <textarea
          id={panel ? 'comment-panel' : 'comment'}
          rows={panel ? 2 : 3}
          maxLength={2000}
          className="textarea resize-none text-base"
          placeholder="Write a comment…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter makes a new line. Skip while an IME is composing.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) submit(e);
          }}
        />
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <div className="flex items-center justify-between gap-2">
          <span className="hidden text-xs text-muted-foreground lg:inline">Enter to send · Shift + Enter for a new line</span>
          <button type="submit" className="btn-secondary ms-auto" disabled={!comment.trim() || add.isPending}>
            <Icon name="send" size={18} /> {add.isPending ? 'Sending…' : 'Comment'}
          </button>
        </div>
      </form>
    </section>
  );
}
